import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatCard } from "@cat-digital-blocks/react/bedrock/components/Card";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatIconWarningTriangle } from "@cat-digital-blocks/react/bedrock/components/Icons/WarningTriangle";
import { CatIconInfoFilled } from "@cat-digital-blocks/react/bedrock/components/Icons/InfoFilled";
import { CatIconInfo } from "@cat-digital-blocks/react/bedrock/components/Icons/Info";
import { CatIconClose } from "@cat-digital-blocks/react/bedrock/components/Icons/Close";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatTable } from "@cat-digital-blocks/react/bedrock/components/Table";
import { CatTableBody } from "@cat-digital-blocks/react/bedrock/components/TableBody";
import { CatTableCell } from "@cat-digital-blocks/react/bedrock/components/TableCell";
import { CatTableHeader } from "@cat-digital-blocks/react/bedrock/components/TableHeader";
import { CatTableHeaderCell } from "@cat-digital-blocks/react/bedrock/components/TableHeaderCell";
import { CatTableObject } from "@cat-digital-blocks/react/bedrock/components/TableObject";
import { CatTableRow } from "@cat-digital-blocks/react/bedrock/components/TableRow";
import { CatDropdown } from "@cat-digital-blocks/react/bedrock/components/Dropdown";
import { CatInputField } from "@cat-digital-blocks/react/bedrock/components/InputField";
import { CatNumericInput } from "@cat-digital-blocks/react/bedrock/components/NumericInput";
import { CatToggle } from "@cat-digital-blocks/react/bedrock/components/Toggle";
import { CatList } from "@cat-digital-blocks/react/bedrock/components/List";
import { CatListItem } from "@cat-digital-blocks/react/bedrock/components/ListItem";
import { CatCheckboxField } from "@cat-digital-blocks/react/bedrock/components/CheckboxField";
import { CatDatepickerField } from "@cat-digital-blocks/react/bedrock/components/DatepickerField";
import styles from "./PALConfiguration.module.scss";
import {
  useGetFieldDefinitionWorkstreamsQuery,
  useGetFieldDefinitionsQuery,
  useGetOperatorsQuery,
  useCreateSimulationMutation,
  useAddSimulationRuleMutation,
  useUpdateSimulationRuleMutation,
  useDeleteSimulationMutation,
  useGetSimulationsQuery,
  useGetSimulationRulesQuery,
  useSubmitSimulationMutation,
  useLazyGetSimulationRulesQuery,
} from "@services/dcmApi";
import type {
  FieldDefinitionResponse,
  OperatorResponse,
  SimulationResponse,
  ConfigurationRuleResponse,
} from "@/types/api";
import type {
  ParameterValue,
  ParameterValues,
  OperatorValues,
  ConditionJSON,
  SimulationResults,
  SimulationResultRow,
  RuleJSON,
} from "@/types/simulation";
import { isRangeValue, generateConditionJSON } from "@/types/simulation";
import { REVERSE_OPERATOR_MAP } from "@/shared/utils/operatorUtils";
import {
  ChangesModal,
  ResultsModal,
  NewSimulationModal,
  UnsavedChangesModal,
  NewSimulationConfirmModal,
} from "@/components/common/Modals/SimulationModals";
import { deriveErrorMessage } from "@/shared/utils/errorUtils";
import {
  validateFields,
  hasValidationErrors,
  getFirstValidationError,
} from "@/shared/utils/validationUtils";
import {
  NUMERIC_TYPE_CONFIG,
  TYPES_WITH_OPERATOR,
  TIMING,
} from "@/shared/constants";
import { logger } from "@/shared/utils/logger";

interface PALConfigurationProps {
  dealerCode: string;
  dealerName?: string;
  currentUserName?: string;
  currentUserFullName?: string;
}

// Translation keys for reusable components
interface TranslationStrings {
  and: string;
  selectValue: string;
  enterValue: string;
  removeValue: string;
  addValue: string;
  trueLabel: string;
  falseLabel: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  selectDate: string;
  enterNumber: string;
  enterWholeNumber: string;
  selectValues: string;
}

// Reusable component for between range inputs
const renderBetweenInputs = (
  fieldName: string,
  value: ParameterValue,
  onChange: (fieldName: string, value: ParameterValue) => void,
  styles: Record<string, string>,
  translations: TranslationStrings,
  suffix?: string,
  allowDecimal: boolean = true,
  disabled?: boolean,
) => {
  const rangeVal = isRangeValue(value) ? value : { from: null, to: null };
  const fromVal = rangeVal.from ?? rangeVal.min ?? null;
  const toVal = rangeVal.to ?? rangeVal.max ?? null;

  const inputElement = (
    placeholder: string,
    val: number | string | null,
    onInputChange: (newVal: number | null) => void,
    inputSuffix?: string,
  ) => (
    <CatNumericInput
      label=""
      hideLabel
      value={val !== null && val !== undefined ? Number(val) : undefined}
      onBlNumericInputChange={(e) => {
        const newVal = e.detail?.value;
        onInputChange(newVal !== undefined && newVal !== null ? newVal : null);
      }}
      step={allowDecimal ? 0.01 : 1}
      placeholder={placeholder}
      disabled={disabled}
      size="sm"
    >
      {inputSuffix && <span slot="suffix">{inputSuffix}</span>}
    </CatNumericInput>
  );

  const fromInput = inputElement(
    translations.fromPlaceholder,
    fromVal,
    (newVal) => onChange(fieldName, { from: newVal, to: toVal }),
    suffix,
  );
  const toInput = inputElement(
    translations.toPlaceholder,
    toVal,
    (newVal) => onChange(fieldName, { from: fromVal, to: newVal }),
    suffix,
  );

  return (
    <div className={styles.betweenInputs}>
      {fromInput}
      <span className={styles.betweenSeparator}>{translations.and}</span>
      {toInput}
    </div>
  );
};

// Reusable component for multi-value IN inputs
const renderMultiValueInputs = (
  fieldName: string,
  value: ParameterValue,
  onChange: (fieldName: string, value: ParameterValue) => void,
  styles: Record<string, string>,
  translations: TranslationStrings,
  inputType: "text" | "number" = "text",
  allowDecimal: boolean = true,
  disabled?: boolean,
) => {
  const getArrayValues = (): string[] => {
    if (Array.isArray(value)) {
      return value.map(String);
    }
    if (value !== null && value !== undefined && value !== "") {
      return [String(value)];
    }
    return [""];
  };

  const values = getArrayValues();

  const processNumericValues = (rawValues: string[]): number[] => {
    return rawValues.map((v) => {
      const num = Number(v);
      if (!allowDecimal && !Number.isInteger(num)) {
        return Math.round(num);
      }
      return num;
    });
  };

  return (
    <div className={styles.multiValueInputs}>
      {values.map((val, index) => (
        <div key={index} className={styles.multiValueRow}>
          {inputType === "number" ? (
            <CatNumericInput
              label=""
              hideLabel
              value={val !== "" ? Number(val) : undefined}
              onBlNumericInputChange={(e) => {
                const newValues = [...values];
                const newVal = e.detail?.value;
                newValues[index] =
                  newVal !== undefined && newVal !== null ? String(newVal) : "";
                const filtered = newValues.filter((v) => v !== "");
                onChange(
                  fieldName,
                  filtered.length > 0 ? processNumericValues(filtered) : null,
                );
              }}
              step={allowDecimal ? 0.01 : 1}
              placeholder={`Value ${index + 1}`}
              disabled={disabled}
              size="sm"
            />
          ) : (
            <CatInputField
              fieldId={`${fieldName}-${index}`}
              label=""
              hideLabel
              value={val}
              onBlChange={(e) => {
                const newValues = [...values];
                newValues[index] = e.detail?.value ?? "";
                const filtered = newValues.filter((v) => v !== "");
                onChange(fieldName, filtered.length > 0 ? filtered : null);
              }}
              placeholder={`Value ${index + 1}`}
              disabled={disabled}
              size="sm"
            />
          )}
          {values.length > 1 && !disabled && (
            <CatButton
              variant="danger"
              size="sm"
              onClick={() => {
                const newValues = values.filter((_, i) => i !== index);
                const filtered = newValues.filter((v) => v !== "");
                onChange(
                  fieldName,
                  filtered.length > 0
                    ? inputType === "number"
                      ? processNumericValues(filtered)
                      : filtered
                    : null,
                );
              }}
              title={translations.removeValue}
            >
              <CatIconClose size="sm" />
            </CatButton>
          )}
        </div>
      ))}
      {!disabled && (
        <CatButton
          variant="primary"
          size="sm"
          onClick={() => onChange(fieldName, [...values, ""])}
          title={translations.addValue}
        >
          + Add
        </CatButton>
      )}
    </div>
  );
};

const renderValueInput = (
  field: FieldDefinitionResponse,
  value: ParameterValue,
  onChange: (fieldName: string, value: ParameterValue) => void,
  styles: Record<string, string>,
  translations: TranslationStrings,
  selectedOperator?: string,
  disabled?: boolean,
) => {
  const fieldName = field.fieldName ?? "";
  const dataType = field.dataType?.toLowerCase() ?? "string";
  const allowedValues = field.allowedValues ?? [];
  const isBetween = selectedOperator?.toLowerCase() === "between";
  const isIn = selectedOperator?.toLowerCase() === "in";

  // Handle boolean type - toggle switch
  if (dataType === "boolean") {
    const isChecked = value === true;
    return (
      <CatToggle
        label=""
        hideLabel
        checked={isChecked}
        onBlToggleChange={(e) =>
          onChange(fieldName, e.detail?.checked ?? false)
        }
        disabled={disabled}
      >
        <span slot="checked-label">{translations.trueLabel}</span>
        <span slot="unchecked-label">{translations.falseLabel}</span>
      </CatToggle>
    );
  }

  // Handle allowed values - single or multi-select
  if (allowedValues.length > 0) {
    const isSingleSelect = field.isMultiSelect === false;

    if (isSingleSelect) {
      return (
        <CatDropdown
          label=""
          size="sm"
          placeholder={translations.selectValue}
          value={value?.toString() ?? ""}
          onBlSelect={(e) => {
            const selectedValue = e.detail?.value;
            const val =
              typeof selectedValue === "string"
                ? selectedValue
                : selectedValue?.value;
            onChange(fieldName, val ?? null);
          }}
          disabled={disabled}
        >
          <CatList spacing="padded">
            {allowedValues.map((allowedVal) => (
              <CatListItem key={allowedVal} value={allowedVal}>
                {allowedVal}
              </CatListItem>
            ))}
          </CatList>
        </CatDropdown>
      );
    }

    // Multi-select dropdown
    const selectedValues = Array.isArray(value)
      ? value.map(String)
      : value
        ? [String(value)]
        : [];

    return (
      <CatDropdown
        label=""
        hideLabel
        size="sm"
        placeholder={translations?.selectValue || "Select values"}
        isMultiSelect={true}
        disabled={disabled}
        onBlDropdownMultiSelect={(e) => {
          const selectedItems = e.detail?.values;
          if (Array.isArray(selectedItems) && selectedItems.length > 0) {
            const newValues = selectedItems.map((item) =>
              typeof item === "string" ? item : item.value,
            );
            onChange(fieldName, newValues);
          } else {
            onChange(fieldName, null);
          }
        }}
      >
        {allowedValues.map((av) => (
          <CatCheckboxField
            key={av}
            value={av}
            checked={selectedValues.includes(av)}
            hasHover
          >
            {av}
          </CatCheckboxField>
        ))}
      </CatDropdown>
    );
  }

  // Handle numeric types with optional suffix
  const numericConfig = NUMERIC_TYPE_CONFIG[dataType];
  if (numericConfig) {
    const { suffix, allowDecimal } = numericConfig;

    if (isBetween) {
      return renderBetweenInputs(
        fieldName,
        value,
        onChange,
        styles,
        translations,
        suffix || undefined,
        allowDecimal,
        disabled,
      );
    }

    if (isIn) {
      return renderMultiValueInputs(
        fieldName,
        value,
        onChange,
        styles,
        translations,
        "number",
        allowDecimal,
        disabled,
      );
    }

    const input = (
      <CatNumericInput
        label=""
        hideLabel
        value={
          value !== null && value !== undefined ? Number(value) : undefined
        }
        onBlNumericInputChange={(e) => {
          const newVal = e.detail?.value;
          if (newVal === undefined || newVal === null) {
            onChange(fieldName, null);
            return;
          }
          if (!allowDecimal && !Number.isInteger(newVal)) {
            onChange(fieldName, Math.round(newVal));
            return;
          }
          onChange(fieldName, newVal);
        }}
        step={allowDecimal ? 0.01 : 1}
        placeholder={
          allowDecimal
            ? translations.enterNumber
            : translations.enterWholeNumber
        }
        disabled={disabled}
        size="sm"
      >
        {suffix && <span slot="suffix">{suffix}</span>}
      </CatNumericInput>
    );

    return input;
  }

  // Handle date types
  if (dataType === "date") {
    return (
      <CatDatepickerField
        label=""
        hideLabel
        placeholder={translations.selectDate}
        value={value?.toString() ?? ""}
        onBlDatePickerChange={(e) =>
          onChange(fieldName, e.detail?.value || null)
        }
        disabled={disabled}
        size="sm"
      />
    );
  }

  if (dataType === "Date") {
    return (
      <CatDatepickerField
        label=""
        hideLabel
        placeholder={translations.selectDate}
        value={value?.toString() ?? ""}
        onBlDatePickerChange={(e) =>
          onChange(fieldName, e.detail?.value || null)
        }
        disabled={disabled}
        size="sm"
      />
    );
  }

  // Default: string type
  if (isIn) {
    return renderMultiValueInputs(
      fieldName,
      value,
      onChange,
      styles,
      translations,
      "text",
      true,
      disabled,
    );
  }

  return (
    <CatInputField
      fieldId={fieldName}
      label=""
      hideLabel
      value={value?.toString() ?? ""}
      onBlChange={(e) => onChange(fieldName, e.detail?.value || null)}
      placeholder={translations.enterValue}
      disabled={disabled}
      size="sm"
    />
  );
};

const PALConfiguration = ({
  dealerCode,
  dealerName,
  currentUserName,
  currentUserFullName,
}: PALConfigurationProps) => {
  const { t } = useTranslation();

  // Translation strings for reusable input components
  const translations: TranslationStrings = {
    and: t("parameters.and"),
    selectValue: t("parameters.selectValue"),
    enterValue: t("parameters.enterValue"),
    removeValue: t("parameters.removeValue"),
    addValue: t("parameters.addValue"),
    trueLabel: t("common.true"),
    falseLabel: t("common.false"),
    fromPlaceholder: t("parameters.fromPlaceholder"),
    toPlaceholder: t("parameters.toPlaceholder"),
    selectDate: t("parameters.selectDate"),
    enterNumber: t("parameters.enterNumber"),
    enterWholeNumber: t("parameters.enterWholeNumber"),
    selectValues: t("parameters.selectValues"),
  };

  // Helper to display creator's name - show full name if it's the current user
  const getCreatorDisplayName = (createdBy: string | undefined): string => {
    if (!createdBy) return t("common.unknown");
    // If the creator matches the current user's username, show their full name
    if (currentUserFullName && createdBy === currentUserName) {
      return currentUserFullName;
    }
    return createdBy;
  };

  const [selectedWorkstream, setSelectedWorkstream] = useState<string>("");
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [isSimulationActive, setIsSimulationActive] = useState(false);
  const [parameterValues, setParameterValues] = useState<ParameterValues>({});
  const [operatorValues, setOperatorValues] = useState<OperatorValues>({});
  const [initialParameterValues, setInitialParameterValues] =
    useState<ParameterValues>({});
  const [initialOperatorValues, setInitialOperatorValues] =
    useState<OperatorValues>({});
  const [isSaving, setIsSaving] = useState(false);
  const [currentSimulationId, setCurrentSimulationId] = useState<string | null>(
    null,
  );
  const [selectedDraftSimulation, setSelectedDraftSimulation] =
    useState<SimulationResponse | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showNewSimulationConfirm, setShowNewSimulationConfirm] =
    useState(false);
  const [showNewSimulationModal, setShowNewSimulationModal] = useState(false);
  const [simulationName, setSimulationName] = useState<string>("");
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingWorkstream, setPendingWorkstream] = useState<string | null>(
    null,
  );
  const [pendingBackNavigation, setPendingBackNavigation] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedSimulationResults, setSelectedSimulationResults] =
    useState<SimulationResults | null>(null);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [selectedSimulationChanges, setSelectedSimulationChanges] = useState<{
    simulation: SimulationResponse;
    rules: ConfigurationRuleResponse[];
  } | null>(null);
  const [isLoadingChanges, setIsLoadingChanges] = useState(false);

  const shouldFetch = isSimulationActive;

  // Mutations for saving
  const [createSimulation] = useCreateSimulationMutation();
  const [addSimulationRule] = useAddSimulationRuleMutation();
  const [updateSimulationRule] = useUpdateSimulationRuleMutation();
  const [deleteSimulation] = useDeleteSimulationMutation();
  const [submitSimulation] = useSubmitSimulationMutation();

  // Lazy query for fetching simulation rules on demand (for results modal)
  const [fetchSimulationRules] = useLazyGetSimulationRulesQuery();

  // Fetch existing simulations to find DRAFT ones - always fetch to show on start screen
  const { data: simulations = [], isFetching: isFetchingSimulations } =
    useGetSimulationsQuery(
      { authoringDealerCode: dealerCode },
      { skip: !dealerCode }, // Skip API call until dealer code is available
    );

  // Filter DRAFT simulations
  const draftSimulations = useMemo(() => {
    return simulations.filter(
      (sim) => sim.simulationStatus?.toUpperCase() === "DRAFT",
    );
  }, [simulations]);

  // Filter SUBMITTED simulations
  const submittedSimulations = useMemo(() => {
    return simulations.filter(
      (sim) => sim.simulationStatus?.toUpperCase() === "SUBMITTED",
    );
  }, [simulations]);

  // Filter COMPLETED simulations - ordered by latest first (descending by updatedAt/createdAt)
  const completedSimulations = useMemo(() => {
    return simulations
      .filter((sim) => sim.simulationStatus?.toUpperCase() === "COMPLETED")
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return dateB - dateA; // Descending order (latest first)
      });
  }, [simulations]);

  // Fetch rules for selected draft simulation OR current simulation (after first save)
  const {
    data: simulationRules = [],
    isFetching: isFetchingRules,
    refetch: refetchRules,
  } = useGetSimulationRulesQuery(
    {
      authoringDealerCode: dealerCode,
      simulationId:
        selectedDraftSimulation?.simulationId ?? currentSimulationId ?? "",
    },
    {
      skip: !(selectedDraftSimulation?.simulationId || currentSimulationId),
    },
  );

  const {
    data: workstreamOptions = [],
    isFetching: isFetchingWorkstreams,
    isError: isWorkstreamsError,
    error: workstreamsError,
  } = useGetFieldDefinitionWorkstreamsQuery(
    { authoringDealerCode: dealerCode },
    { skip: !shouldFetch },
  );

  const { data: operators = [], isFetching: isFetchingOperators } =
    useGetOperatorsQuery(undefined, { skip: !shouldFetch });

  const {
    data: fieldDefinitions = [],
    isFetching: isFetchingFields,
    isError: isFieldsError,
    error: fieldsError,
  } = useGetFieldDefinitionsQuery(
    {
      authoringDealerCode: dealerCode,
      workstream: selectedWorkstream || undefined,
    },
    { skip: !shouldFetch || !selectedWorkstream },
  );

  // Group operators by data type for quick lookup
  const operatorsByDataType = useMemo(() => {
    const grouped: Record<string, OperatorResponse[]> = {};
    operators.forEach((op) => {
      const dt = op.dataType?.toLowerCase() ?? "string";
      if (!grouped[dt]) {
        grouped[dt] = [];
      }
      grouped[dt].push(op);
    });
    return grouped;
  }, [operators]);

  // Helper to find matching operator from available operators (case-insensitive)
  const findMatchingOperator = useCallback(
    (jsonOperator: string, fieldDataType: string): string => {
      // First, try reverse mapping to get UI format
      const uiOperator =
        REVERSE_OPERATOR_MAP[jsonOperator] ??
        REVERSE_OPERATOR_MAP[jsonOperator.toUpperCase()] ??
        jsonOperator;

      // Get available operators for this data type
      const availableOps =
        operatorsByDataType[fieldDataType.toLowerCase()] ?? [];

      // Try exact match first
      const exactMatch = availableOps.find((op) => op.operator === uiOperator);
      if (exactMatch) {
        return exactMatch.operator ?? uiOperator;
      }

      // Try case-insensitive match
      const caseInsensitiveMatch = availableOps.find(
        (op) => op.operator?.toUpperCase() === uiOperator.toUpperCase(),
      );
      if (caseInsensitiveMatch) {
        return caseInsensitiveMatch.operator ?? uiOperator;
      }

      // Return the UI operator as fallback
      return uiOperator;
    },
    [operatorsByDataType],
  );

  // Helper to parse conditions from saved rules and populate form
  const parseConditionsFromRules = useCallback(
    (
      rules: Record<string, unknown>,
    ): {
      values: ParameterValues;
      operators: OperatorValues;
    } => {
      const values: ParameterValues = {};
      const ops: OperatorValues = {};

      const parseCondition = (condition: Record<string, unknown>) => {
        if (condition.type === "CONDITION") {
          const field = condition.field as string;
          const operator = condition.operator as string;
          const value = condition.value as ParameterValue;

          if (field) {
            values[field] = value;
            if (operator) {
              // Find the field definition to get data type
              const fieldDef = fieldDefinitions.find(
                (f) => f.fieldName === field,
              );
              const dataType = fieldDef?.dataType ?? "string";
              // Map back from JSON operator to UI operator, matching available options
              ops[field] = findMatchingOperator(operator, dataType);
            }
          }
        } else if (
          condition.type === "GROUP" &&
          Array.isArray(condition.rules)
        ) {
          (condition.rules as Record<string, unknown>[]).forEach(
            parseCondition,
          );
        }
      };

      parseCondition(rules);
      return { values, operators: ops };
    },
    [fieldDefinitions, findMatchingOperator],
  );

  // Load saved values when simulation rules are fetched (for drafts or after first save)
  useEffect(() => {
    const hasSimulation = selectedDraftSimulation || currentSimulationId;

    if (hasSimulation && simulationRules.length > 0 && selectedWorkstream) {
      // Find rules for the selected workstream
      const workstreamRule = simulationRules.find(
        (rule) => rule.workstream === selectedWorkstream,
      );

      if (workstreamRule?.simulationRule) {
        const { values, operators: ops } = parseConditionsFromRules(
          workstreamRule.simulationRule,
        );
        setParameterValues(values);
        setOperatorValues(ops);
        // Store initial values for change tracking
        setInitialParameterValues(values);
        setInitialOperatorValues(ops);
      }
      // Note: Don't reset to empty here - let the workstream change effect handle that
    }
  }, [
    selectedDraftSimulation,
    currentSimulationId,
    simulationRules,
    selectedWorkstream,
    parseConditionsFromRules,
  ]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    // Helper to normalize empty values for comparison
    const normalizeValue = (val: ParameterValue): string => {
      if (val === null || val === undefined || val === "") {
        return "null";
      }
      if (Array.isArray(val) && val.length === 0) {
        return "null";
      }
      return JSON.stringify(val);
    };

    // Check if any parameter value has changed
    const paramKeys = new Set([
      ...Object.keys(parameterValues),
      ...Object.keys(initialParameterValues),
    ]);
    for (const key of paramKeys) {
      const currentNormalized = normalizeValue(parameterValues[key]);
      const initialNormalized = normalizeValue(initialParameterValues[key]);
      if (currentNormalized !== initialNormalized) {
        return true;
      }
    }

    // Check if any operator value has changed
    const opKeys = new Set([
      ...Object.keys(operatorValues),
      ...Object.keys(initialOperatorValues),
    ]);
    for (const key of opKeys) {
      const currentOp = operatorValues[key] || "";
      const initialOp = initialOperatorValues[key] || "";
      if (currentOp !== initialOp) {
        return true;
      }
    }

    return false;
  }, [
    parameterValues,
    operatorValues,
    initialParameterValues,
    initialOperatorValues,
  ]);

  // Helper to get operators for a field's data type
  const getOperatorsForField = (
    field: FieldDefinitionResponse,
  ): OperatorResponse[] => {
    const dataType = field.dataType?.toLowerCase() ?? "string";
    return operatorsByDataType[dataType] ?? [];
  };

  // Helper to check if operator should be shown for a field
  // Show for numeric and string types with single input (no allowed values)
  // Boolean types don't need operators - the value (True/False) is the condition itself
  const shouldShowOperator = (field: FieldDefinitionResponse): boolean => {
    const dataType = field.dataType?.toLowerCase() ?? "string";
    const allowedValues = field.allowedValues ?? [];

    // Boolean types don't need operators
    if (dataType === "boolean" || dataType === "bool") {
      return false;
    }

    return TYPES_WITH_OPERATOR.includes(dataType) && allowedValues.length === 0;
  };

  // Reset parameter values and operator values when workstream changes
  // This effect handles clearing values for new simulations (without saved rules)
  useEffect(() => {
    // Only reset if this is a new simulation (no draft, no current simulation ID)
    // For existing simulations, the loading effect will populate values from saved rules
    if (
      !selectedDraftSimulation &&
      !currentSimulationId &&
      selectedWorkstream
    ) {
      // New simulation, new workstream selected - start fresh
      setParameterValues({});
      setOperatorValues({});
      setInitialParameterValues({});
      setInitialOperatorValues({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally only depend on selectedWorkstream to avoid infinite loops
  }, [selectedWorkstream]);

  useEffect(() => {
    if (!isSimulationActive) {
      setSelectedWorkstream("");
      return;
    }

    if (workstreamOptions.length === 0) {
      return;
    }

    setSelectedWorkstream((current) => {
      if (current && workstreamOptions.includes(current)) {
        return current;
      }
      // Default to first workstream option instead of empty
      return workstreamOptions[0] || "";
    });
  }, [isSimulationActive, workstreamOptions]);

  useEffect(() => {
    if (isSimulationActive && !isFetchingFields) {
      setLastSync(new Date());
    }
  }, [isSimulationActive, isFetchingFields, fieldDefinitions.length]);

  const handleStartSimulation = () => {
    setSelectedDraftSimulation(null);
    setCurrentSimulationId(null);
    setIsSimulationActive(true);
    setLastSync(new Date());
  };

  const handleLoadDraft = (simulation: SimulationResponse) => {
    setSelectedDraftSimulation(simulation);
    setCurrentSimulationId(simulation.simulationId ?? null);
    setSimulationName(simulation.simulationName ?? "");
    setIsSimulationActive(true);
    setLastSync(new Date());
  };

  // Handle showing simulation results
  const handleShowResults = async (simulation: SimulationResponse) => {
    // Fetch simulation rules from database to get workstreams
    try {
      const rulesResult = await fetchSimulationRules({
        authoringDealerCode: dealerCode,
        simulationId: simulation.simulationId ?? "",
      }).unwrap();

      // Get unique workstreams from the simulation rules
      const workstreams = [
        ...new Set(
          rulesResult
            .map((rule: ConfigurationRuleResponse) => rule.workstream)
            .filter(Boolean),
        ),
      ] as string[];

      // Helper function to extract ALL fields from nested rule structures
      const extractAllFieldsFromRule = (
        data: Record<string, unknown>,
      ): string[] => {
        const fields: string[] = [];

        // If it's a simple condition with field
        if (data.field) {
          fields.push(String(data.field));
        }

        // If it's a condition group, look inside rules/conditions array
        const nestedRules = (data.rules || data.conditions) as
          | Record<string, unknown>[]
          | undefined;
        if (nestedRules && Array.isArray(nestedRules)) {
          // Recursively get fields from all nested rules
          for (const nestedRule of nestedRules) {
            fields.push(...extractAllFieldsFromRule(nestedRule));
          }
        }

        return fields;
      };

      // Generate rows from rules - one row per condition (not per rule)
      const generateRowsFromRule = (
        rule: ConfigurationRuleResponse,
      ): SimulationResultRow[] => {
        // Extract ALL parameters (field names) from rule - handle nested structures
        const ruleData = (rule.simulationRule || {}) as Record<string, unknown>;
        const allFields = extractAllFieldsFromRule(ruleData);

        // If no fields found, return single row with dash
        if (allFields.length === 0) {
          const currentRecords = Math.floor(Math.random() * 100) + 20;
          // Always generate newRecords less than currentRecords (10-40% reduction)
          const reductionPercent = Math.random() * 0.3 + 0.1;
          const newRecords = Math.floor(
            currentRecords * (1 - reductionPercent),
          );
          // Dollar impact can be positive (savings) or negative (cost) randomly
          const impactSign = Math.random() > 0.4 ? 1 : -1;
          const dollarImpact =
            impactSign * Math.floor(Math.random() * 50000 + 5000);

          return [
            {
              workstream: rule.workstream || "-",
              currentRecords,
              newRecords,
              dollarImpact,
            },
          ];
        }

        // Generate one row per workstream (aggregated)
        const currentRecords = Math.floor(Math.random() * 100) + 20;
        // Always generate newRecords less than currentRecords (10-40% reduction)
        const reductionPercent = Math.random() * 0.3 + 0.1;
        const newRecords = Math.floor(currentRecords * (1 - reductionPercent));
        // Dollar impact can be positive (savings) or negative (cost) randomly
        const impactSign = Math.random() > 0.4 ? 1 : -1;
        const dollarImpact =
          impactSign * Math.floor(Math.random() * 50000 + 5000);

        return [
          {
            workstream: rule.workstream || "-",
            currentRecords,
            newRecords,
            dollarImpact,
          },
        ];
      };

      // Use dealer code from simulation or props
      const simulationDealerCode = simulation.authoringDealerCode || dealerCode;

      const results: SimulationResults = {
        dealerName: dealerName || simulationDealerCode, // Use dealer name if available, fallback to code
        dealerCode: simulationDealerCode,
        simulationName:
          simulation.simulationName || simulation.simulationId || "Simulation",
        summary: `Simulation completed successfully. Results show impact across ${
          workstreams.length
        } workstream${workstreams.length !== 1 ? "s" : ""}.`,
        rows:
          workstreams.length > 0
            ? rulesResult.flatMap(generateRowsFromRule)
            : [
                {
                  workstream: "No workstreams configured",
                  currentRecords: 0,
                  newRecords: 0,
                  dollarImpact: 0,
                },
              ],
        rules: rulesResult || [],
      };

      setSelectedSimulationResults(results);
      setShowResultsModal(true);
    } catch (error) {
      logger.error("Error fetching simulation rules:", error);
      // Fallback to show modal with error message
      const fallbackDealerCode = simulation.authoringDealerCode || dealerCode;
      setSelectedSimulationResults({
        dealerName: dealerName || fallbackDealerCode,
        dealerCode: fallbackDealerCode,
        simulationName:
          simulation.simulationName || simulation.simulationId || "Simulation",
        summary: "Unable to load simulation results. Please try again.",
        rows: [],
        rules: [],
      });
      setShowResultsModal(true);
    }
  };

  // Handle view changes for a simulation
  const handleViewChanges = async (simulation: SimulationResponse) => {
    setIsLoadingChanges(true);
    try {
      const rulesResult = await fetchSimulationRules({
        authoringDealerCode: simulation.authoringDealerCode || dealerCode,
        simulationId: simulation.simulationId ?? "",
      }).unwrap();

      setSelectedSimulationChanges({
        simulation,
        rules: rulesResult || [],
      });
      setShowChangesModal(true);
    } catch (error) {
      logger.error("Error fetching simulation rules:", error);
      setSelectedSimulationChanges({
        simulation,
        rules: [],
      });
      setShowChangesModal(true);
    } finally {
      setIsLoadingChanges(false);
    }
  };

  // Handle workstream change with unsaved changes check
  const handleWorkstreamChange = (newWorkstream: string) => {
    if (hasChanges && selectedWorkstream) {
      // User has unsaved changes - show confirmation modal
      setPendingWorkstream(newWorkstream);
      setShowUnsavedChangesModal(true);
    } else {
      // No unsaved changes - proceed with change
      setSelectedWorkstream(newWorkstream);
    }
  };

  // Confirm discarding unsaved changes and proceed with workstream change or back navigation
  const handleDiscardChanges = () => {
    // Clear any validation errors when discarding changes
    setValidationError(null);

    if (pendingBackNavigation) {
      // User confirmed going back - discard changes and navigate
      setParameterValues({});
      setOperatorValues({});
      setInitialParameterValues({});
      setInitialOperatorValues({});
      setIsSimulationActive(false);
      setSelectedDraftSimulation(null);
      setSelectedWorkstream("");
      setPendingBackNavigation(false);
    } else if (pendingWorkstream !== null) {
      setParameterValues({});
      setOperatorValues({});
      setInitialParameterValues({});
      setInitialOperatorValues({});
      setSelectedWorkstream(pendingWorkstream);
    }
    setShowUnsavedChangesModal(false);
    setPendingWorkstream(null);
  };

  // Cancel workstream change or back navigation and keep current values
  const handleCancelWorkstreamChange = () => {
    setShowUnsavedChangesModal(false);
    setPendingWorkstream(null);
    setPendingBackNavigation(false);
  };

  const handleParameterValueChange = (
    fieldName: string,
    value: ParameterValue,
  ) => {
    setParameterValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleOperatorChange = (fieldName: string, operator: string) => {
    setOperatorValues((prev) => ({
      ...prev,
      [fieldName]: operator,
    }));
  };

  // Helper function to compare values (handles arrays and primitives)
  const valuesAreEqual = (val1: ParameterValue, val2: unknown): boolean => {
    // Both null/undefined
    if (val1 == null && val2 == null) return true;
    if (val1 == null || val2 == null) return false;

    // Array comparison
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      const sorted1 = [...val1].sort();
      const sorted2 = [...(val2 as unknown[])].map(String).sort();
      return sorted1.every((v, i) => String(v) === sorted2[i]);
    }

    // Convert to string for comparison
    return String(val1) === String(val2);
  };

  // Helper to generate condition for a field - only if different from default
  const generateFieldCondition = (
    field: FieldDefinitionResponse,
  ): ConditionJSON | null => {
    const fieldName = field.fieldName ?? "";
    const value = parameterValues[fieldName];
    const operator = operatorValues[fieldName];

    if (value === null || value === undefined) {
      return null;
    }

    // Get default value from field definition
    const defaultValue = field.defaultValue?.value;
    const defaultOperator = field.defaultValue?.operator;

    // Debug logging
    logger.debug(`generateFieldCondition: ${fieldName}`, {
      value,
      operator,
      defaultValue,
      defaultOperator,
      hasDefaultValue: defaultValue !== undefined,
    });

    // Check if value matches default - if so, don't save (no change)
    if (defaultValue !== undefined) {
      const valueMatches = valuesAreEqual(value, defaultValue);
      const operatorMatches =
        !operator ||
        !defaultOperator ||
        operator.toUpperCase() === defaultOperator.toUpperCase();

      logger.debug(
        `  - valueMatches: ${valueMatches}, operatorMatches: ${operatorMatches}`,
      );

      if (valueMatches && operatorMatches) {
        // Value matches default - no change to save
        logger.debug(`  - SKIPPING (matches default)`);
        return null;
      }
    }

    const allowedValues = field.allowedValues ?? [];
    if (allowedValues.length > 0 && Array.isArray(value)) {
      return generateConditionJSON(fieldName, value, "IN");
    }
    if (allowedValues.length > 0 && !Array.isArray(value)) {
      return generateConditionJSON(fieldName, value, "=");
    }

    const dataType = field.dataType?.toLowerCase() ?? "string";
    if (dataType === "boolean" || dataType === "bool") {
      return generateConditionJSON(fieldName, value, "=");
    }

    if (operator) {
      return generateConditionJSON(fieldName, value, operator);
    }

    return null;
  };

  // Generate conditions JSON for all fields with values
  const handleSave = async () => {
    const conditionsMap = new Map<string, ConditionJSON>();

    // Validate all fields using common validation utility
    const validationResult = validateFields(
      visibleFields,
      operatorValues,
      parameterValues,
    );

    // Show validation errors if any
    if (hasValidationErrors(validationResult)) {
      const errorMessage = getFirstValidationError(validationResult);
      if (errorMessage) {
        setValidationError(errorMessage);
      }
      return;
    }

    // Use Map to ensure only one condition per field (last value wins for duplicates)
    visibleFields.forEach((field) => {
      const fieldName = field.fieldName ?? "";
      if (!fieldName) return;

      const condition = generateFieldCondition(field);
      if (condition) {
        // Replace any existing condition for this field
        conditionsMap.set(fieldName, condition);
      }
    });

    const conditions = Array.from(conditionsMap.values());

    if (conditions.length === 0) {
      setValidationError(
        "No conditions to save. Please enter values for at least one parameter.",
      );
      return;
    }

    // Clear any previous validation error
    setValidationError(null);

    // Build final structure
    let rules: RuleJSON;
    if (conditions.length === 1) {
      rules = conditions[0];
    } else {
      rules = {
        type: "GROUP",
        operator: "AND",
        rules: conditions,
      };
    }

    setIsSaving(true);

    try {
      let simulationId = currentSimulationId;

      // Step 1: Create simulation only if not already created and no existing draft
      // If editing an existing draft, use that simulation ID
      if (!simulationId) {
        // Validate simulation name is provided for new simulations
        if (!simulationName.trim()) {
          alert("Please enter a simulation name before saving.");
          setIsSaving(false);
          return;
        }

        // Delete any existing draft simulations for this dealer before creating new one
        if (draftSimulations.length > 0) {
          for (const draft of draftSimulations) {
            if (draft.simulationId) {
              try {
                await deleteSimulation({
                  authoringDealerCode: dealerCode,
                  simulationId: draft.simulationId,
                }).unwrap();
              } catch (deleteError) {
                logger.error(
                  `Failed to delete draft ${draft.simulationId}:`,
                  deleteError,
                );
                // Continue with creation even if delete fails
              }
            }
          }
        }

        // Create new simulation (creates with DRAFT status)
        const simulationResponse = await createSimulation({
          authoringDealerCode: dealerCode,
          body: {
            createdBy: currentUserName || "unknown",
            simulationName: simulationName.trim(),
          },
        }).unwrap();

        simulationId = simulationResponse.simulationId ?? null;
        setCurrentSimulationId(simulationId);

        if (!simulationId) {
          throw new Error("Failed to create simulation - no ID returned");
        }
      }

      // Step 2: Check if rule for this workstream already exists
      const existingRule = simulationRules.find(
        (rule) => rule.workstream === selectedWorkstream,
      );

      if (existingRule?.simulationRuleId) {
        // Update existing rule
        await updateSimulationRule({
          authoringDealerCode: dealerCode,
          simulationId: simulationId,
          ruleId: existingRule.simulationRuleId,
          body: {
            workstream: selectedWorkstream,
            description: `PAL Configuration for ${selectedWorkstream}`,
            simulationRule: rules as unknown as Record<string, unknown>,
          },
        }).unwrap();
      } else {
        // Add new rule
        await addSimulationRule({
          authoringDealerCode: dealerCode,
          simulationId: simulationId,
          body: {
            workstream: selectedWorkstream,
            description: `PAL Configuration for ${selectedWorkstream}`,
            simulationRule: rules as unknown as Record<string, unknown>,
          },
        }).unwrap();
      }

      // Refetch rules to get the updated/new rule for subsequent saves
      await refetchRules();

      setSuccessMessage(`Configuration saved successfully.`);

      // Update initial values to current values (reset change tracking)
      setInitialParameterValues({ ...parameterValues });
      setInitialOperatorValues({ ...operatorValues });

      // Auto-dismiss success message
      setTimeout(
        () => setSuccessMessage(null),
        TIMING.SUCCESS_MESSAGE_DURATION,
      );
    } catch (error) {
      logger.error("Error saving configuration:", error);
      const errorMsg = deriveErrorMessage(
        error,
        "Failed to save configuration. Please try again.",
      );
      alert(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle submitting the simulation
  const handleSubmitSimulation = async () => {
    if (!currentSimulationId) {
      alert("Please save the simulation first before starting.");
      return;
    }

    setIsSubmitting(true);

    try {
      await submitSimulation({
        authoringDealerCode: dealerCode,
        simulationId: currentSimulationId,
        body: {
          submittedBy: currentUserName || "unknown",
        },
      }).unwrap();

      setIsSubmitted(true);
      setSuccessMessage(t("messages.submitSuccess"));

      // Auto-dismiss success message
      setTimeout(
        () => setSuccessMessage(null),
        TIMING.SUCCESS_MESSAGE_DURATION,
      );
    } catch (error) {
      logger.error("Error submitting simulation:", error);
      const errorMsg = deriveErrorMessage(
        error,
        "Failed to submit simulation. Please try again.",
      );
      alert(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const visibleFields: FieldDefinitionResponse[] = fieldDefinitions;
  const isLoading =
    isFetchingFields ||
    isFetchingWorkstreams ||
    isFetchingOperators ||
    isFetchingSimulations ||
    isFetchingRules;
  const totalFields = visibleFields.length;
  const errorMessage = isWorkstreamsError
    ? deriveErrorMessage(workstreamsError, "Unable to load workstreams.")
    : isFieldsError
      ? deriveErrorMessage(fieldsError, "Unable to load field definitions.")
      : null;

  return (
    <div className={styles.container}>
      <CatHeading variant="headline">{t("palParameters.title")}</CatHeading>

      {/* New Simulation Modal - available on both start screen and draft screen */}
      {showNewSimulationModal && (
        <NewSimulationModal
          styles={styles}
          simulationName={simulationName}
          onSimulationNameChange={setSimulationName}
          existingDraftName={
            draftSimulations.length > 0
              ? draftSimulations[0]?.simulationName
              : undefined
          }
          onClose={() => setShowNewSimulationModal(false)}
          onConfirm={() => {
            setShowNewSimulationModal(false);
            handleStartSimulation();
          }}
        />
      )}

      {!isSimulationActive && (
        <>
          <div className={styles.simulationCta}>
            <div>
              <CatHeading variant="title-sm">
                {t("palParameters.readyToRun")}
              </CatHeading>
              <p>{t("palParameters.readyToRunDescription")}</p>
            </div>
            <div className={styles.simulationActions}>
              <CatButton
                variant="primary"
                disabled={submittedSimulations.length > 0}
                onClick={() => {
                  setSimulationName("");
                  setShowNewSimulationModal(true);
                }}
                title={
                  submittedSimulations.length > 0
                    ? t("simulation.cannotCreateWhileSubmitted")
                    : undefined
                }
              >
                {t("palParameters.newSimulation")}
              </CatButton>
              {draftSimulations.length > 0 && (
                <div className={styles.draftSection}>
                  <label className={styles.label}>
                    {t("simulation.orContinueDraft")}
                  </label>
                  <CatDropdown
                    label=""
                    size="sm"
                    placeholder={t("simulation.selectDraft")}
                    onBlSelect={(e) => {
                      const sim = draftSimulations.find(
                        (s) => s.simulationId === e.detail?.value,
                      );
                      if (sim) handleLoadDraft(sim);
                    }}
                  >
                    <CatList spacing="padded">
                      {draftSimulations.map((sim) => (
                        <CatListItem
                          key={sim.simulationId}
                          value={sim.simulationId}
                        >
                          {sim.simulationName || sim.simulationId} - Created:{" "}
                          {sim.createdAt
                            ? new Date(sim.createdAt).toLocaleDateString()
                            : t("common.unknown")}
                        </CatListItem>
                      ))}
                    </CatList>
                  </CatDropdown>
                </div>
              )}
            </div>
          </div>

          {/* Submitted Simulations Section - only show if there are submitted simulations */}
          {submittedSimulations.length > 0 && (
            <div className={styles.approvedSimulationsSection}>
              <CatHeading variant="title-sm" className={styles.sectionTitle}>
                {t("simulation.submittedSimulations")}
              </CatHeading>
              <div className={styles.simulationsList}>
                {submittedSimulations.map((sim) => (
                  <div key={sim.simulationId} className={styles.simulationCard}>
                    <div className={styles.simulationCardHeader}>
                      <span className={styles.simulationName}>
                        {sim.simulationName || sim.simulationId}
                      </span>
                      <span
                        className={
                          styles.statusBadge + " " + styles.statusSubmittedBadge
                        }
                      >
                        {t("simulation.status.submitted")}
                      </span>
                    </div>
                    <div className={styles.simulationCardDetails}>
                      <span>
                        {t("simulation.createdBy")}:{" "}
                        {getCreatorDisplayName(sim.createdBy)}
                      </span>
                      <span>
                        {t("simulation.createdAt")}:{" "}
                        {sim.createdAt
                          ? new Date(sim.createdAt).toLocaleDateString()
                          : t("common.unknown")}
                      </span>
                      {sim.updatedAt && (
                        <span>
                          {t("simulation.submittedAt")}:{" "}
                          {new Date(sim.updatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className={styles.simulationCardActions}>
                      <CatButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleViewChanges(sim)}
                      >
                        {t("simulation.viewChanges")}
                      </CatButton>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completed Simulations Section - only show if there are completed simulations */}
          {completedSimulations.length > 0 && (
            <div className={styles.approvedSimulationsSection}>
              <CatHeading variant="title-sm" className={styles.sectionTitle}>
                {t("simulation.completedSimulations")}
              </CatHeading>
              <div className={styles.simulationsList}>
                {completedSimulations.map((sim) => (
                  <div key={sim.simulationId} className={styles.simulationCard}>
                    <div className={styles.simulationCardHeader}>
                      <span className={styles.simulationName}>
                        {sim.simulationName || sim.simulationId}
                      </span>
                      <span
                        className={
                          styles.statusBadge + " " + styles.statusCompletedBadge
                        }
                      >
                        {t("simulation.status.completed")}
                      </span>
                    </div>
                    <div className={styles.simulationCardDetails}>
                      <span>
                        {t("simulation.createdBy")}:{" "}
                        {getCreatorDisplayName(sim.createdBy)}
                      </span>
                      <span>
                        {t("simulation.createdAt")}:{" "}
                        {sim.createdAt
                          ? new Date(sim.createdAt).toLocaleDateString()
                          : t("common.unknown")}
                      </span>
                      {sim.updatedAt && (
                        <span>
                          {t("simulation.completedAt")}:{" "}
                          {new Date(sim.updatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className={styles.simulationCardActions}>
                      <CatButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleViewChanges(sim)}
                      >
                        {t("simulation.viewChanges")}
                      </CatButton>
                      <CatButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleShowResults(sim)}
                      >
                        {t("simulation.viewResults")}
                      </CatButton>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {isSimulationActive && (
        <>
          <div className={styles.toolbar}>
            <div className={styles.formControl}>
              <CatHeading variant="label-lg">
                {t("palParameters.workstream")}
              </CatHeading>
              <CatDropdown
                label=""
                size="sm"
                placeholder={t("palParameters.selectWorkstream")}
                value={selectedWorkstream}
                onBlSelect={(e) => {
                  const selectedValue = e.detail?.value;
                  const val =
                    typeof selectedValue === "string"
                      ? selectedValue
                      : (selectedValue?.value ?? "");
                  handleWorkstreamChange(val);
                }}
                disabled={workstreamOptions.length === 0}
              >
                <CatList spacing="padded">
                  {workstreamOptions.map((workstream) => (
                    <CatListItem key={workstream} value={workstream}>
                      {workstream}
                    </CatListItem>
                  ))}
                </CatList>
              </CatDropdown>
            </div>
            <CatButton
              variant="contrast"
              onClick={() => {
                // Check for unsaved changes before going back
                if (hasChanges) {
                  setPendingBackNavigation(true);
                  setShowUnsavedChangesModal(true);
                } else {
                  // No unsaved changes - proceed with navigation
                  setIsSimulationActive(false);
                  setSelectedDraftSimulation(null);
                  setSelectedWorkstream("");
                }
              }}
            >
              ← {t("common.back")}
            </CatButton>
            {selectedDraftSimulation && (
              <CatButton
                variant="primary"
                disabled={submittedSimulations.length > 0}
                onClick={() => {
                  setSimulationName("");
                  setShowNewSimulationConfirm(true);
                }}
                title={
                  submittedSimulations.length > 0
                    ? t("simulation.cannotCreateWhileSubmitted")
                    : undefined
                }
              >
                {t("palParameters.newSimulation")}
              </CatButton>
            )}
          </div>

          {/* New Simulation Confirmation Modal */}
          {showNewSimulationConfirm && (
            <NewSimulationConfirmModal
              styles={styles}
              simulationName={simulationName}
              onSimulationNameChange={setSimulationName}
              currentDraftName={
                selectedDraftSimulation?.simulationName ||
                selectedDraftSimulation?.simulationId
              }
              onClose={() => setShowNewSimulationConfirm(false)}
              onConfirm={() => {
                setShowNewSimulationConfirm(false);
                setSelectedDraftSimulation(null);
                setCurrentSimulationId(null);
                setParameterValues({});
                setOperatorValues({});
                setInitialParameterValues({});
                setInitialOperatorValues({});
              }}
            />
          )}

          {/* Unsaved Changes Confirmation Modal */}
          {showUnsavedChangesModal && (
            <UnsavedChangesModal
              styles={styles}
              isBackNavigation={pendingBackNavigation}
              onClose={handleCancelWorkstreamChange}
              onDiscard={handleDiscardChanges}
            />
          )}

          <div className={styles.statusBar}>
            <span>
              {isLoading
                ? t("common.loading")
                : selectedWorkstream
                  ? t("palParameters.parametersLoaded", { count: totalFields })
                  : t("palParameters.selectWorkstreamPrompt")}
            </span>
            {selectedDraftSimulation && (
              <span className={styles.draftBadge}>
                {t("palParameters.editing")}:{" "}
                {selectedDraftSimulation.simulationName ||
                  selectedDraftSimulation.simulationId}
              </span>
            )}
            {currentSimulationId && !selectedDraftSimulation && (
              <span className={styles.draftBadge}>
                {t("palParameters.new")}:{" "}
                {simulationName || currentSimulationId}
              </span>
            )}
            {lastSync && (
              <span className={styles.timestamp}>
                {t("palParameters.lastSynced")}: {lastSync.toLocaleTimeString()}
              </span>
            )}
          </div>

          {successMessage && (
            <CatInlineNotification
              heading={successMessage}
              dismissible
              onBlInlineNotificationClose={() => setSuccessMessage(null)}
            >
              <CatIconInfoFilled slot="before" />
            </CatInlineNotification>
          )}

          {validationError && (
            <CatInlineNotification
              heading={validationError}
              variant="error"
              dismissible
              onBlInlineNotificationClose={() => setValidationError(null)}
            >
              <CatIconInfoFilled slot="before" />
            </CatInlineNotification>
          )}

          {errorMessage && (
            <CatInlineNotification heading={errorMessage} variant="error">
              <CatIconWarningTriangle slot="before" />
            </CatInlineNotification>
          )}

          {selectedWorkstream && (
            <CatCard className={styles.card}>
              <div className={styles.tableWrapper}>
                <CatTableObject>
                  <CatTable aria-label={t("parameters.tableAriaLabel")}>
                    <CatTableHeader isFrozen={true}>
                      <CatTableRow>
                        <CatTableHeaderCell width="20%">
                          {t("parameters.fieldName")}
                        </CatTableHeaderCell>
                        <CatTableHeaderCell width="12%">
                          {t("parameters.defaultOperator")}
                        </CatTableHeaderCell>
                        <CatTableHeaderCell width="18%">
                          {t("parameters.defaultValue")}
                        </CatTableHeaderCell>
                        <CatTableHeaderCell width="15%">
                          {t("parameters.operator")}
                        </CatTableHeaderCell>
                        <CatTableHeaderCell width="35%">
                          {t("parameters.value")}
                        </CatTableHeaderCell>
                      </CatTableRow>
                    </CatTableHeader>

                    <CatTableBody>
                      {visibleFields.map((field) => (
                        <CatTableRow
                          key={
                            field.fieldId ??
                            field.fieldName ??
                            crypto.randomUUID()
                          }
                        >
                          <CatTableCell>
                            <span className={styles.parameterName}>
                              {field.fieldName ?? "—"}
                            </span>
                          </CatTableCell>
                          <CatTableCell>
                            <span className={styles.defaultValue}>
                              {field.defaultValue?.operator
                                ? (REVERSE_OPERATOR_MAP[
                                    field.defaultValue.operator
                                  ] ??
                                  REVERSE_OPERATOR_MAP[
                                    field.defaultValue.operator.toUpperCase()
                                  ] ??
                                  field.defaultValue.operator)
                                : field.allowedValues &&
                                    field.allowedValues.length > 0 &&
                                    !field.isMultiSelect
                                  ? "="
                                  : "—"}
                            </span>
                          </CatTableCell>
                          <CatTableCell>
                            <span className={styles.defaultValue}>
                              {field.defaultValue?.value !== undefined
                                ? // Handle boolean values with Init Caps
                                  typeof field.defaultValue.value === "boolean"
                                  ? field.defaultValue.value
                                    ? "True"
                                    : "False"
                                  : Array.isArray(field.defaultValue.value)
                                    ? (
                                        field.defaultValue.value as string[]
                                      ).join(", ")
                                    : typeof field.defaultValue.value ===
                                          "object" &&
                                        field.defaultValue.value !== null
                                      ? // Handle BETWEEN values with from/to
                                        (
                                          field.defaultValue.value as {
                                            from?: number | string;
                                            to?: number | string;
                                          }
                                        ).from !== undefined
                                        ? `${
                                            (
                                              field.defaultValue.value as {
                                                from?: number | string;
                                              }
                                            ).from
                                          } - ${
                                            (
                                              field.defaultValue.value as {
                                                to?: number | string;
                                              }
                                            ).to
                                          }`
                                        : JSON.stringify(
                                            field.defaultValue.value,
                                          )
                                      : // Handle string "true"/"false" with Init Caps
                                        String(
                                            field.defaultValue.value,
                                          ).toLowerCase() === "true"
                                        ? "True"
                                        : String(
                                              field.defaultValue.value,
                                            ).toLowerCase() === "false"
                                          ? "False"
                                          : String(field.defaultValue.value)
                                : "—"}
                            </span>
                          </CatTableCell>
                          <CatTableCell>
                            {shouldShowOperator(field) ? (
                              <CatDropdown
                                label=""
                                size="sm"
                                placeholder={t("common.select")}
                                value={
                                  operatorValues[field.fieldName ?? ""] ?? ""
                                }
                                onBlSelect={(e) => {
                                  const selectedValue = e.detail?.value;
                                  const val =
                                    typeof selectedValue === "string"
                                      ? selectedValue
                                      : (selectedValue?.value ?? "");
                                  handleOperatorChange(
                                    field.fieldName ?? "",
                                    val,
                                  );
                                }}
                                disabled={isSubmitted}
                              >
                                <CatList spacing="padded">
                                  {getOperatorsForField(field).map((op) => (
                                    <CatListItem
                                      key={op.operator}
                                      value={op.operator ?? ""}
                                    >
                                      {op.operator}
                                    </CatListItem>
                                  ))}
                                </CatList>
                              </CatDropdown>
                            ) : (field.allowedValues ?? []).length > 0 ? (
                              field.isMultiSelect ? (
                                <span className={styles.operatorLabel}>IN</span>
                              ) : (
                                <span className={styles.operatorLabel}>=</span>
                              )
                            ) : (
                              <span className={styles.operatorPlaceholder}>
                                —
                              </span>
                            )}
                          </CatTableCell>
                          <CatTableCell>
                            <div className={styles.valueCell}>
                              {renderValueInput(
                                field,
                                parameterValues[field.fieldName ?? ""],
                                handleParameterValueChange,
                                styles,
                                translations,
                                operatorValues[field.fieldName ?? ""],
                                isSubmitted,
                              )}
                            </div>
                          </CatTableCell>
                        </CatTableRow>
                      ))}
                    </CatTableBody>
                  </CatTable>
                </CatTableObject>
              </div>
              {!isLoading && visibleFields.length === 0 && (
                <div className={styles.emptyState}>
                  <CatIconInfo aria-hidden="true" />
                  <p>{t("parameters.noParameters")}</p>
                </div>
              )}

              <div className={styles.actionBar}>
                <CatButton
                  variant="contrast"
                  onClick={() => {
                    setParameterValues({});
                    setOperatorValues({});
                    setCurrentSimulationId(null);
                  }}
                  disabled={isSaving || isSubmitted}
                >
                  {t("common.cancel")}
                </CatButton>
                <CatButton
                  variant="primary"
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges || isSubmitted}
                >
                  {isSaving ? t("common.saving") : t("common.save")}
                </CatButton>
                <CatButton
                  variant="primary"
                  onClick={handleSubmitSimulation}
                  disabled={
                    !currentSimulationId ||
                    hasChanges ||
                    isSubmitting ||
                    isSubmitted
                  }
                >
                  {isSubmitting
                    ? t("common.submitting")
                    : isSubmitted
                      ? t("simulation.status.submitted")
                      : t("palParameters.startSimulation")}
                </CatButton>
              </div>
            </CatCard>
          )}
        </>
      )}

      {/* Simulation Results Modal */}
      {showResultsModal && selectedSimulationResults && (
        <ResultsModal
          styles={styles}
          results={selectedSimulationResults}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedSimulationResults(null);
          }}
        />
      )}

      {/* View Changes Modal */}
      {showChangesModal && selectedSimulationChanges && (
        <ChangesModal
          styles={styles}
          simulation={selectedSimulationChanges.simulation}
          rules={selectedSimulationChanges.rules}
          isLoading={isLoadingChanges}
          onClose={() => {
            setShowChangesModal(false);
            setSelectedSimulationChanges(null);
          }}
        />
      )}
    </div>
  );
};

PALConfiguration.displayName = "PALConfiguration";

export default PALConfiguration;
